"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { BadgeCheck } from "lucide-react";
import ArtistFollowButton from "./ArtistFollowButton";
import { profileBannerUrl, profileImageUrl } from "@/lib/profile-image";
import type { Artist } from "./types";

type Props = {
  artist: Artist; lang: "fa" | "en";
  /** The list was read under the viewer's session, so each row's `follow_state` is theirs. */
  personal?: boolean;
};

function initials(value: string) {
  return Array.from(value.replace(/^NB-User-#?/i, "").trim())[0]?.toLocaleUpperCase() || "?";
}

export default function ArtistDirectoryCard({ artist, lang, personal = false }: Props) {
  const [coverFailed, setCoverFailed] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const isFa = lang === "fa";
  const name = artist.name?.trim() || artist.username?.trim() || (isFa ? "هنرمند" : "Artist");
  const username = artist.username?.trim().replace(/^@/, "") || "";
  const href = `/${lang}/artists/${encodeURIComponent(username || String(artist.id))}`;
  // The backend's shared default banner reads as none: the card draws its own art instead.
  const cover = profileBannerUrl(artist.profile?.banner);
  const avatar = profileImageUrl(artist.profile?.cover);
  const followers = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US").format(artist.following?.follower ?? 0);
  const following = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US").format(artist.following?.following ?? 0);

  return (
    <article className="v2-artists-card">
      <Link href={href} className="v2-artists-card-link v2-focus" aria-label={isFa ? `مشاهده پروفایل ${name}` : `View ${name}'s profile`} />
      <div className="v2-artists-card-media">
        {cover && !coverFailed ? (
          <Image src={cover} alt="" fill sizes="(max-width: 619px) 50vw, (max-width: 1299px) 46vw, 31vw" onError={() => setCoverFailed(true)} />
        ) : (
          <div className="v2-artists-card-media-fallback" aria-hidden />
        )}
      </div>

      <div className="v2-artists-card-content">
        <div className="v2-artists-card-identity">
          <span className="v2-artists-card-avatar" aria-hidden>
            {avatar && !avatarFailed ? <Image src={avatar} alt="" width={48} height={48} onError={() => setAvatarFailed(true)} /> : initials(name)}
          </span>
          <div className="v2-artists-card-person">
            <div className="v2-artists-card-name-row">
              <h3 title={name}>{name}</h3>
              {Number(artist.blue_check) === 1 ? <BadgeCheck aria-label={isFa ? "تأییدشده" : "Verified"} /> : null}
            </div>
            <p className="v2-artists-card-handle" dir="ltr" title={username ? `@${username}` : undefined}>
              {username ? `@${username}` : "NFTBaz"}
            </p>
          </div>
        </div>

        <div className="v2-artists-card-foot">
          <div className="v2-artists-card-stats">
            <div><strong>{followers}</strong><span>{isFa ? "دنبال‌کننده" : "Followers"}</span></div>
            <div><strong>{following}</strong><span>{isFa ? "دنبال‌شده" : "Following"}</span></div>
          </div>
          {username ? (
            <div className="v2-artists-card-follow">
              <ArtistFollowButton username={username} isFa={isFa} fromList initialState={personal && (artist.follow_state === "FOLLOWED" || artist.follow_state === "NOT_FOLLOWED") ? artist.follow_state : undefined} />
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}
